import { MouseEvent, ReactNode, useCallback, forwardRef, useMemo } from 'react';
import { DataTable } from './DataTable';
import {
  DataTableBaseColumn,
  DataTableRow,
  DataTableSortableProps,
  DataTableVariant,
  RenderRow,
  SxRowFunction,
} from './TableTypes';
import { Collapse, TableCell, TableRow as MuiTableRow, TableRowProps, Stack } from '@mui/material';
import { ChevronDown, ChevronUp } from '@sweep-io/sweep-design/dist/icons';
import { colors } from '@sweep-io/sweep-design';
import TableRow from './TableRow';
import { TableVirtuoso } from 'react-virtuoso';
import { TableLoader } from './TableLoader';
import { useVirtualScrollTable } from './useVirtualScrollTable';
import { useVirtualTableComponents } from './hooks/useVirtualTableComponents';

/**
 * DataTableExpandableRows
 *
 * A table component that supports expandable rows, showing additional content when a row is clicked.
 *
 * How to use:
 * 1. Create rows with expandable content:
 *    ```
 *    const rows = [
 *      {
 *        id: '1',
 *        name: 'Row 1',
 *        // other data...
 *        expandableContent: <YourExpandableContentComponent data={...} />
 *      }
 *    ]
 *    ```
 *
 * 2. Implement state for tracking expanded row:
 *    ```
 *    const [expandedRowId, setExpandedRowId] = useState<string>();
 *    ```
 *
 * 3. Use the component:
 *    ```
 *    <DataTableExpandableRows
 *      rows={rows}
 *      columns={columns}
 *      expandedRowId={expandedRowId}
 *      setExpandedRowId={setExpandedRowId}
 *      isVirtualScroll={true} // Optional: Enable virtual scrolling for large datasets
 *    />
 *    ```
 *
 * Implementation details:
 * - The component works by duplicating each row into two entries:
 *   1. The base row that displays the actual data with a chevron icon
 *   2. An expandable row that contains the expandable content
 *
 * - Each row gets enhanced with expandableContentProps:
 *   - __isExpanded: Tracks if this row is currently expanded
 *   - __chevron: For base rows, contains the up/down chevron icon
 *   - __parentRowId: For expandable rows, references the ID of the parent row
 *
 * - When a row is clicked, it toggles expansion by setting expandedRowId
 *
 */

export interface ExpandableDataTableRow extends DataTableRow {
  expandableContent: ReactNode;
  __chevron?: ReactNode;
}

export interface EnhancedRow extends ExpandableDataTableRow {
  expandableContentProps?: {
    __isExpanded: boolean;
    __parentRowId?: string; //only relevant for the "expandableContent" row
  };
}

export interface DataTableExpandableRowsProps<TRow extends ExpandableDataTableRow>
  extends Omit<DataTableSortableProps<TRow>, 'allowRowReorder'> {
  expandedRowId?: string;
  setExpandedRowId: (rowId: string) => void;
  isVirtualScroll?: boolean;
  noTableBorder?: boolean;
  removeChevronColumn?: boolean;
}

const CHEVRON_COLUMN: DataTableBaseColumn = {
  field: '__chevron',
  headerName: '',
  width: '16px',
  minWidth: '16px',
};

const removeBorderAndSxRow = (sxRowFunction?: SxRowFunction) => {
  const inputSx = sxRowFunction?.(false) ?? {};
  return {
    '.MuiTableCell-root': {
      borderBottom: 'none!important',
    },
    ...inputSx,
  };
};

export function DataTableExpandableRows<TRow extends ExpandableDataTableRow>({
  rows,
  columns,
  expandedRowId,
  setExpandedRowId,
  onRowClick,
  isVirtualScroll = false,
  tableId,
  removeChevronColumn,
  ...restTableProps
}: DataTableExpandableRowsProps<TRow>) {
  const {
    containerRef,
    onSortChange,
    defaultSortState,
    tableEmptyStateJsx,
    isLoading,
    variant = DataTableVariant.medium,
    hideChevronButton,
    noTableBorder,
    sxRowFunction,
  } = restTableProps;

  const enhancedColumns = useMemo(
    () => (removeChevronColumn ? columns : [CHEVRON_COLUMN, ...columns]),
    [columns, removeChevronColumn],
  );

  const enhancedRows: EnhancedRow[] = useMemo(
    () =>
      rows.flatMap((row) => {
        const isExpanded = expandedRowId === row.id;
        const chevronIcon = isExpanded ? (
          <ChevronUp color={colors.grey[700]} size="medium" />
        ) : (
          <ChevronDown size="medium" color={colors.grey[700]} />
        );
        const baseRow: EnhancedRow = {
          ...row,
          __chevron: row.expandableContent ? chevronIcon : undefined,
          expandableContentProps: {
            __isExpanded: isExpanded,
          },
        };

        const expandableRow: EnhancedRow = {
          ...row,
          id: `${row.id}-expanded`,
          expandableContentProps: {
            __isExpanded: isExpanded,
            __parentRowId: row.id,
          },
        };

        return [baseRow, expandableRow];
      }),
    [rows, expandedRowId],
  );

  const handleRowClick = useCallback(
    (e: MouseEvent<HTMLTableCellElement>, row: any, colData: any) => {
      setExpandedRowId(expandedRowId === row.id ? undefined : row?.id);
      onRowClick?.(e, row, colData);
    },
    [expandedRowId, onRowClick, setExpandedRowId],
  );

  const renderRowFn: RenderRow = useCallback(
    ({ row, rowKey }) => {
      const isExpanded = row.expandableContentProps.__isExpanded;
      const isBaseRow = !row.expandableContentProps.__parentRowId;
      if (isBaseRow) {
        return (
          <TableRow
            key={rowKey}
            hideChevronButton
            row={{ ...row, isRowActive: isExpanded }}
            columns={enhancedColumns}
            variant={variant}
            onClick={(e, colData) => handleRowClick(e, row, colData)}
            sxRowFunction={() => removeBorderAndSxRow(sxRowFunction)}
          />
        );
      }
      return (
        <MuiTableRow
          sx={{
            backgroundColor: colors.white, //override table's default
          }}
        >
          <TableCell
            colSpan={enhancedColumns.length + 1}
            sx={{
              padding: 0,
              borderBottom: isExpanded ? undefined : 'none',
              '&:hover': {
                backgroundColor: colors.white, //override table's default
              },
            }}
          >
            <Collapse in={isExpanded}>{row?.expandableContent}</Collapse>
          </TableCell>
        </MuiTableRow>
      );
    },
    [enhancedColumns, variant, handleRowClick, sxRowFunction],
  );

  const virtualScrollProps = useVirtualScrollTable({
    columns: enhancedColumns,
    rows: enhancedRows,
    containerRef,
    onSortChange,
    defaultSortState,
    tableId,
    variant,
    enabled: isVirtualScroll,
  });

  const CustomTableRow = useMemo(() => {
    return forwardRef<any, TableRowProps>((props: any, ref) => {
      const row = props.item;
      const idx = props['data-index'];

      const isExpanded = row.expandableContentProps.__isExpanded;
      const isBaseRow = !row.expandableContentProps.__parentRowId;

      if (isBaseRow) {
        return (
          <TableRow
            hideChevronButton={hideChevronButton}
            data-index={idx}
            ref={ref}
            row={{ ...row, isRowActive: isExpanded }}
            columns={virtualScrollProps.noActionColumns}
            sxRowFunction={() => removeBorderAndSxRow(sxRowFunction)}
            tableRowProps={{ id: `table_row_${row.id}` }}
            onClick={(e, column) => handleRowClick(e, row, column)}
            variant={variant}
            internalColumnWidths={virtualScrollProps.internalColumnWidths}
            {...props}
          />
        );
      }

      return (
        <MuiTableRow
          ref={ref}
          sx={{
            backgroundColor: colors.white, //override table's default
          }}
          {...props}
        >
          <TableCell
            colSpan={virtualScrollProps.noActionColumns.length + 1}
            sx={{
              padding: 0,
              borderBottom: isExpanded ? undefined : 'none',
              '&:hover': {
                backgroundColor: colors.white, //override table's default
              },
            }}
          >
            <Collapse in={isExpanded}>{row?.expandableContent}</Collapse>
          </TableCell>
        </MuiTableRow>
      );
    });
  }, [
    hideChevronButton,
    virtualScrollProps.noActionColumns,
    virtualScrollProps.internalColumnWidths,
    variant,
    sxRowFunction,
    handleRowClick,
  ]);

  const components = useVirtualTableComponents({
    rows: enhancedRows,
    tableEmptyStateJsx,
    customTableRow: CustomTableRow,
  });

  if (isVirtualScroll) {
    return (
      <Stack
        height="100%"
        position="relative"
        sx={{
          border: noTableBorder ? 'none' : `1px solid ${colors.grey[120]}`,
          borderRadius: noTableBorder ? 'none' : '8px',
          overflow: 'hidden',
        }}
      >
        {isLoading && <TableLoader />}

        <TableVirtuoso
          ref={virtualScrollProps.tableRef}
          totalCount={enhancedRows.length}
          data={enhancedRows}
          components={components}
          fixedHeaderContent={virtualScrollProps.renderHeader}
          increaseViewportBy={{ top: 0, bottom: 1 }} // added to fix SWEEP-7583
        />
      </Stack>
    );
  }

  return (
    <DataTable
      {...restTableProps}
      columns={enhancedColumns}
      rows={enhancedRows}
      renderRow={renderRowFn}
      allowRowReorder={false}
      variant={variant}
      tableId={tableId}
    />
  );
}
